import { useQuery, useMutation, useInfiniteQuery } from "@tanstack/react-query";
import { queryClient } from "@/lib/query-client";
import {
  apiClient,
  appClient,
  authorizedApiClient,
  demoAuthorizedApiClient,
} from "../api-client";
import type {
  ApiResponse,
  SquadListResponse,
  MatchListResponse,
  TokenListResponse,
  MatchDetailsResponse,
  MatchHistoryResponse,
  ScheduleTaskRequest,
  ScheduleTaskResponse,
  AISquadSuggestionResponse,
  LeaderboardResponse,
  SeasonalLeaderboardResponse,
  StatsResponse,
  Match,
  TournamentListResponse,
  Tournament,
  TournamentDetailsResponse,
  TournamentScheduleResponse,
  Mission,
  ReferralListResponse,
  ChallengeListResponse,
  ChallengeResponse,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  Shop,
  ShopListResponse,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  InventoryItem,
  InventoryListResponse,
  RouletteResponse,
  LeaderboardReferralResponse,
  QueueListResponse,
  StatsSummaryResponse,
  KothHistoryItem,
  SquadPerformanceResponse,
  RushLeaderboardResponse,
  ChangeLogResponse,
  KothLeaderboardResponse,
  DailyMmrLeaderboardResponse,
  ChampionshipListApiResponse,
  ChampionshipDetailsResponse,
  GachaClaimResponse,
  GachaPrizeResponse,
  GachaHistoryItem,
  ItemDetailsResponse,
  LuckyDrawDetailByDateResponse,
  LuckyDrawHistoryDetails,
  LuckyDrawHistoryResponse,
  LuckyDrawLeaderboardResponse,
  LuckyDrawParticipateResponse,
  LuckyDrawPrizesResponse,
  LuckyDrawTicketsResponse,
  ShareMissionListResponse,
  SubmitReportRequest,
  SubmitReportResponse,
  SubmitTokenReportRequest,
  SubmitTokenReportResponse,
  CanReportPlayerStatusItem,
  CanReportTokenStatusItem,
  ChampionshipBracketResponse,
  OnboardingMissionsResponse,
  WeeklyBonusResponse,
  StreakMissionResponse,
  StreakMissionClaimRequest,
  MilestoneStreakResponse,
  DailyStreakResponse,
  WeeklyStreakResponse,
  HotspotEntry,
  CalendarEventsResponse,
  ThemeResponse,
  ShareXOAuthStatusResponse,
  ShareXOAuthTokenRequest,
  SharePostAndClaimResponse,
  RequestJoinFreeResponse,
  PreviewSponsoredMatchResponse,
  MatchmakingCancelPayload,
  MatchmakingCancelResponse,
  MatchmakingDetails,
  MatchmakingHistoryRow,
  MatchmakingRequestPayload,
  MatchmakingRequestResponse,
  MatchmakingRequestSponsoredResponse,
  VipResponse,
  VipClaimResponse,
} from "../types/api";
import configs from "../configs";
import { useGameSigner } from "./useGameSigner";

export { queryClient };

// 1a. Get List Squads
export const useSquadList = (userId: number | undefined) => {
  return useQuery({
    queryKey: ["squads", userId],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiResponse<SquadListResponse[]>>(
        `/squad/get-list-squads?userId=${userId}`,
      );
      return data.data;
    },
    enabled: userId != null,
  });
};

//1b. Infinite squads list with cursor pagination
export const useSquadListInfinite = (
  userId: number | undefined,
  {
    squadName,
    limit = 10,
    cursor,
  }: { squadName?: string; limit?: number; cursor?: string } = {},
) => {
  return useInfiniteQuery({
    queryKey: ["squads", "infinite", userId, squadName, limit],
    queryFn: async ({ pageParam = cursor ?? "" }) => {
      const params = new URLSearchParams({
        userId: String(userId),
        limit: String(limit),
      });
      if (squadName) params.append("squadName", squadName);
      if (pageParam) params.append("cursor", String(pageParam));

      const { data } = await apiClient.get<ApiResponse<SquadListResponse[]>>(
        `/squad/get-list-squads?${params.toString()}`,
      );

      return {
        squads: data.data,
        nextCursor: data.pagination?.nextCursor ?? null,
        hasMore: data.pagination?.hasMore ?? false,
      };
    },
    initialPageParam: cursor ?? "",
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: userId != null,
  });
};

// 2. Get List Matches
export const useMatchList = (filter = "live", cursor = "") => {
  return useQuery({
    queryKey: ["matches", filter],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiResponse<MatchListResponse>>(
        `/match/get-list-matches?filter=${filter}&cursor=${cursor}`,
      );
      return data.data;
    },
  });
};

// 3. Token List
export const useTokenList = (page = 1, limit = 120, assetType?: string) => {
  return useQuery({
    queryKey: ["tokens", page, limit, assetType],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });
      if (assetType) params.append("assetType", assetType);
      const { data } = await apiClient.get<ApiResponse<TokenListResponse[]>>(
        `/token/list?${params.toString()}`,
      );
      return data.data;
    },
  });
};

// 4. Stock List
export const useStockList = (page = 1, limit = 120, { enabled = true }: { enabled?: boolean } = {}) => {
  return useQuery({
    queryKey: ["stocks", page, limit],
    enabled,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data } = await apiClient.get<ApiResponse<TokenListResponse[]>>(
        `/token/list?page=${page}&limit=${limit}&assetType=stocks`,
      );
      return {
        tokens: data.data,
        marketStatus: data.usStockMarket ?? null,
      };
    },
  });
};

// Token Dashboard — chart history
export const useTokenHistory = (ticker: string, interval: string) => {
  return useQuery({
    queryKey: ["token-history", ticker, interval],
    enabled: !!ticker,
    staleTime: 30_000,
    queryFn: async () => {
      const { data } = await apiClient.get<ApiResponse<import("@/lib/types/token-dashboard").TokenHistoryResponse>>(
        `/token-dashboard/${ticker}/history?interval=${interval}`,
      );
      return data.data;
    },
  });
};

// 5. Schedule Task
export const useScheduleTask = () => {
  return useMutation({
    mutationFn: async (taskData: ScheduleTaskRequest) => {
      const { data } = await apiClient.post<ApiResponse<ScheduleTaskResponse>>(
        "/trigger/task",
        taskData,
      );
      return data.data;
    },
    onSuccess: () => {
      // Invalidate relevant queries when a new task is scheduled
    },
  });
};

// 5. Match Details
export const useMatchDetails = (matchId: number | string) => {
  return useQuery({
    queryKey: ["match", matchId],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiResponse<MatchDetailsResponse>>(
        `/match/details?matchId=${matchId}`,
      );
      return data.data;
    },
    enabled: !!matchId,
    refetchInterval: 2500, // 2.5s
  });
};

// 5b. Latest Practice
export const useLatestPractice = () => {
  return useQuery({
    queryKey: ["latestPractice"],
    queryFn: async () => {
      const { data } = await apiClient.get<
        ApiResponse<TournamentDetailsResponse>
      >("/match/latest-practice");
      return data.data;
    },
    refetchInterval: false,
  });
};

// 6. Match History
export const useMatchHistory = (userId: number | undefined) => {
  return useQuery({
    queryKey: ["matchHistory", userId],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiResponse<MatchHistoryResponse>>(
        `/match/history?userId=${userId}`,
      );
      return data.data;
    },
    enabled: userId != null,
  });
};

// 7. AI Suggestion
export const useAISquadSuggestion = ({
  value,
  riskMode,
}: {
  value: number;
  riskMode: string;
}) => {
  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.get<
        ApiResponse<AISquadSuggestionResponse>
      >(`/ai/suggest-squad?opponentSquadValue=${value}&riskMode=${riskMode}`);
      return data.data;
    },
  });
};

// 8. Infinite Match List
export const useInfiniteMatchList = (filter = "available") => {
  return useInfiniteQuery({
    queryKey: ["infiniteMatches", filter],
    queryFn: async ({ pageParam = "" }) => {
      const { data } = await apiClient.get<ApiResponse<MatchListResponse>>(
        `/match/get-list-matches?filter=${filter}&cursor=${pageParam}`,
      );
      return {
        matches: data.data.matches,
        totalMatches: data.data.totalMatches,
        totalOpenBet: data.data.totalOpenBet,
        nextCursor: data.pagination?.nextCursor,
        hasMore: data.pagination?.hasMore || false,
      };
    },
    initialPageParam: "",
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });
};

// 10. Leaderboard
export const useLeaderboardList = (limit: number = 50, tier?: number, index?: number) => {
  return useQuery({
    queryKey: ["leaderboard", limit, tier, index],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set("limit", String(limit));
      if (tier !== undefined) params.set("tier", String(tier));
      if (index !== undefined) params.set("index", String(index));
      const { data } = await apiClient.get<ApiResponse<LeaderboardResponse>>(
        `/leaderboard/list?${params.toString()}`,
      );
      return data.data;
    },
  });
};

// 11. Stats
export const useStats = (userPublicKey: string, forceRefresh = false) => {
  return useQuery({
    queryKey: ["stats", userPublicKey, forceRefresh],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiResponse<StatsResponse>>(
        `/user/stats?userPublicKey=${userPublicKey}&forceRefresh=${forceRefresh}`,
      );
      return data.data;
    },
    enabled: !!userPublicKey,
  });
};

// 12. Find Match
export const useFindMatch = () => {
  return useMutation({
    mutationFn: async (matchData: {
      squadId: string;
      duration: number;
      matchType: string;
    }) => {
      const client = await authorizedApiClient();
      const { data } = await client.post<ApiResponse<Match>>(
        "/match/find",
        matchData,
      );
      return data.data;
    },
    onSuccess: () => {
      // Optionally invalidate any queries related to matches or perform other actions
      queryClient.invalidateQueries({ queryKey: ["findMatch"] });
    },
  });
};

// 12b. Find Match (Demo)
export const useDemoFindMatch = () => {
  return useMutation({
    mutationFn: async (matchData: {
      squadId: string;
      duration: number;
      matchType: string;
    }) => {
      const client = await demoAuthorizedApiClient();
      const { data } = await client.post<ApiResponse<Match>>(
        "/match/find",
        matchData,
      );
      return data.data;
    },
    onSuccess: () => {
      // Optionally invalidate any queries related to matches or perform other actions
      queryClient.invalidateQueries({ queryKey: ["findMatch"] });
    },
  });
};

// 13. Create Squad
export const useCreateSquad = () => {
  return useMutation({
    mutationFn: async (squadData: {
      name: string;
      playerIds: number[];
      isOneTimeUsed?: boolean;
    }) => {
      const client = await authorizedApiClient();
      const { data } = await client.post<ApiResponse<SquadListResponse>>(
        "/squad/create",
        squadData,
      );
      return data.data;
    },
    onSuccess: () => {
      // Optionally invalidate any queries related to squads or perform other actions
      queryClient.invalidateQueries({ queryKey: ["squads"] });
    },
  });
};

// 13b. Create Squad (Demo)
export const useDemoCreateSquad = () => {
  return useMutation({
    mutationFn: async (squadData: {
      name: string;
      playerIds: number[];
      isOneTimeUsed?: boolean;
    }) => {
      const client = await demoAuthorizedApiClient();
      const { data } = await client.post<ApiResponse<SquadListResponse>>(
        "/squad/create",
        squadData,
      );
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["squads"] });
    },
  });
};

// 14. Cancel Match
export const useCancelMatch = () => {
  return useMutation({
    mutationFn: async (matchData: { matchId: string }) => {
      // Make the POST request to the cancel-match endpoint
      const client = await authorizedApiClient();
      const { data } = await client.post<ApiResponse<{ message: string }>>(
        "/match/cancel",
        matchData,
      );
      return data.data;
    },
    onSuccess: () => {
      // Invalidate any relevant queries when a match is canceled
      queryClient.invalidateQueries({ queryKey: ["infiniteMatches"] });
      queryClient.invalidateQueries({ queryKey: ["matchHistory"] });
      queryClient.invalidateQueries({ queryKey: ["squads"] });
    },
  });
};

// 14.Tournament List
export const useTournamentList = () => {
  return useQuery({
    queryKey: ["tournaments"],
    queryFn: async () => {
      const { data } =
        await apiClient.get<ApiResponse<TournamentListResponse>>(
          `/tournament/list`,
        );
      return data.data;
    },
  });
};

// 16. Tournament Details
export const useTournamentDetails = (
  tournamentId: number | string,
  options?: { refetchInterval?: number | false } | boolean,
) => {
  const opts =
    typeof options === "boolean"
      ? { refetchInterval: options ? (2500 as const) : (false as const) }
      : options;
  const refetchInterval = opts?.refetchInterval ?? undefined;
  return useQuery({
    queryKey: ["tournament", tournamentId],
    queryFn: async () => {
      const { data } = await apiClient.get<
        ApiResponse<TournamentDetailsResponse>
      >(
        `/tournament/detail?tournamentId=${tournamentId}${refetchInterval ? `&timestamp=${new Date().getTime()}` : ""}`,
      );
      return data.data;
    },
    enabled: !!tournamentId,
    refetchInterval: refetchInterval === false ? false : refetchInterval,
  });
};

// 17. Tournament History
export const useTournamentHistory = (
  userId: number | undefined,
  type?:
    | "tournament"
    | "rush"
    | "grand_rush"
    | "wta"
    | "wta_amateur"
    | "wta_semi_pro"
    | "wta_pro"
    | "user_hosted"
    | "user_hosted_championship",
) => {
  return useQuery({
    queryKey: ["tournamentHistory", userId, type],
    queryFn: async () => {
      let url = `/tournament/history?userId=${userId}`;

      if (type) {
        url += `&type=${type}`;
      }

      const { data } = await apiClient.get<ApiResponse<Tournament[]>>(url);
      return data.data;
    },
    enabled: !!userId,
  });
};

// 18. PvP Share Card
export const usePvpShareCard = (matchId: string, status: string) => {
  return useQuery({
    queryKey: ["pvpShareCard", matchId, status],
    queryFn: async () => {
      const response =
        status === "live"
          ? await appClient.get<Blob>(`/api/live-pvp?matchId=${matchId}`, {
              responseType: "blob",
            })
          : await appClient.get<Blob>(`/api/win?matchId=${matchId}`, {
              responseType: "blob",
            });

      const url = URL.createObjectURL(response.data);
      return url;
    },
    enabled: !!matchId,
  });
};

// 19. Tournament Share Card
export const useTournamentShareCard = (
  tournamentId: string,
  status: string,
  userPublicKey?: string,
) => {
  return useQuery({
    queryKey: ["tournamentShareCard", tournamentId, status, userPublicKey],
    queryFn: async () => {
      let url = "";

      if (status === "waiting") {
        const query = new URLSearchParams({ tournamentId });
        url = `/api/waiting-tournament?${query.toString()}`;
      } else if (status === "live") {
        url = `/api/live-tournament?tournamentId=${tournamentId}`;
      } else if (status === "completed") {
        url = `/api/win-tournament?tournamentId=${tournamentId}&userPublicKey=${userPublicKey}`;
      } else {
        throw new Error("Unsupported status");
      }

      const response = await appClient.get<Blob>(url, {
        responseType: "blob",
      });

      return URL.createObjectURL(response.data);
    },
    enabled: !!tournamentId,
  });
};

// 20. PlayerStats Share Card
export const useUserStatsShareCard = (userPublicKey: string) => {
  return useQuery({
    queryKey: ["userStatsShareCard", userPublicKey],
    queryFn: async () => {
      const response = await appClient.get<Blob>(
        `/api/screenshots?userPublicKey=${userPublicKey}&s=${new Date().getTime()}`,
        { responseType: "blob" },
      );

      return URL.createObjectURL(response.data);
    },
    enabled: !!userPublicKey,
  });
};

// missions-v2: daily-streak scope. Returns the streak envelope (missions,
// completionBonus, streakBonus, resetTimestamp). Resets daily — same
// shape as the `daily` slice of the legacy `/mission/streak` response.
export const useDailyStreakMission = (userId: number | undefined) => {
  return useQuery({
    queryKey: ["dailyStreakMission", userId],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiResponse<DailyStreakResponse>>(
        `/mission?scope=daily-streak&userId=${userId}`,
      );
      return data.data;
    },
    enabled: userId != null,
    staleTime: 30_000,
  });
};

// 22. One Time Mission List
// missions-v2: one-time scope. Envelope shape is `data: Mission[]` (plain
// array), unlike the legacy `{ mission: [] }` wrapper.
export const useOneTimeMissionList = (userId: number | undefined) => {
  return useInfiniteQuery({
    queryKey: ["infiniteOneTimeMissions", userId],
    queryFn: async ({ pageParam = "" }) => {
      const { data } = await apiClient.get<ApiResponse<Mission[]>>(
        `/mission?scope=one-time&userId=${userId}&cursor=${pageParam}`,
      );
      return {
        missions: data.data ?? [],
        nextCursor: data.pagination?.nextCursor,
        hasMore: data.pagination?.hasMore || false,
      };
    },
    initialPageParam: "",
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: userId != null,
  });
};

// missions-v2: advanced-pro-reward-weekly scope. Single mission row that
// awards a double-trophies item to pro users (`users.tier >= 1`). Resets
// implicitly on Monday 00:00 UTC. Returns [] until the seed row is deployed.
export const useAdvancedProRewardWeekly = (userId: number | undefined) => {
  return useQuery({
    queryKey: ["advancedProRewardWeekly", userId],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiResponse<Mission[]>>(
        `/mission?scope=advanced-pro-reward-weekly&userId=${userId}`,
      );
      return data.data ?? [];
    },
    enabled: userId != null,
    staleTime: 30_000,
  });
};

// missions-v2: first-login (rookie onboarding) missions. Replaces the old
// `useOneTimeMissionList` + `m.tier === 0` filter pattern. Note: v2's
// envelope wraps a plain `Mission[]` (not the legacy `{ mission: [] }`).
export const useFirstLoginMissionList = (userId: number | undefined) => {
  return useInfiniteQuery({
    queryKey: ["infiniteFirstLoginMissions", userId],
    queryFn: async ({ pageParam = "" }) => {
      const { data } = await apiClient.get<ApiResponse<Mission[]>>(
        `/mission?scope=first-login&userId=${userId}&cursor=${pageParam}`,
      );
      return {
        missions: data.data ?? [],
        nextCursor: data.pagination?.nextCursor,
        hasMore: data.pagination?.hasMore || false,
      };
    },
    initialPageParam: "",
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: userId != null,
  });
};

// 23. Claim Mission
export const useClaimMission = () => {
  return useMutation({
    mutationFn: async (missionData: { missionId: string }) => {
      // Make the POST request to the cancel-match endpoint
      const client = await authorizedApiClient();
      const { data } = await client.post<ApiResponse<{ message: string }>>(
        "/mission/claim",
        missionData,
      );
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["infiniteOneTimeMissions"] });
      queryClient.invalidateQueries({
        queryKey: ["infiniteFirstLoginMissions"],
      });
      queryClient.invalidateQueries({ queryKey: ["dailyStreakMission"] });
      queryClient.invalidateQueries({ queryKey: ["weeklyStreakMission"] });
      queryClient.invalidateQueries({ queryKey: ["milestoneStreakMission"] });
      queryClient.invalidateQueries({ queryKey: ["advancedProRewardWeekly"] });
      queryClient.invalidateQueries({ queryKey: ["stats"] });
    },
  });
};

// 24. Referral List
export const useReferralList = (userId: number | undefined) => {
  return useInfiniteQuery({
    queryKey: ["referral", userId],
    queryFn: async ({ pageParam = "" }) => {
      const { data } = await apiClient.get<ApiResponse<ReferralListResponse[]>>(
        `/user/referral/list?userId=${userId}&cursor=${pageParam}`,
      );
      return {
        friends: data.data,
        nextCursor: data.pagination?.nextCursor,
        hasMore: data.pagination?.hasMore || false,
      };
    },
    initialPageParam: "",
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: userId != null,
  });
};

// 25. Referral
export const useReferral = () => {
  return useMutation({
    mutationFn: async (refData: { referralCode: string }) => {
      const client = await authorizedApiClient();
      const { data } = await client.post<ApiResponse<{ message: string }>>(
        "/user/referral",
        refData,
      );
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["referral"] });
    },
  });
};

// 26. Challenge Friend
export const useChallengeFriend = () => {
  return useMutation({
    mutationFn: async (matchData: {
      squadId: string;
      duration: number;
      matchType: string;
      challengerId: number;
    }) => {
      const client = await authorizedApiClient();
      const { data } = await client.post<ApiResponse<ChallengeResponse>>(
        "/challenge/friend",
        matchData,
      );
      return data.data;
    },
    onSuccess: () => {
      // Optionally invalidate any queries related to matches or perform other actions
      queryClient.invalidateQueries({ queryKey: ["findMatchFriend"] });
    },
  });
};

// 27. Accept Challenge
export const useAcceptChallenge = () => {
  return useMutation({
    mutationFn: async (matchData: {
      squadId: string;
      matchType: string;
      challengeId: string;
    }) => {
      const client = await authorizedApiClient();
      const { data } = await client.post<ApiResponse<Match>>(
        "/challenge/accept",
        matchData,
      );
      return data.data;
    },
    onSuccess: () => {
      // Optionally invalidate any queries related to matches or perform other actions
      queryClient.invalidateQueries({ queryKey: ["challenges"] });
    },
  });
};

// 28. Terminate Challenge
export const useTerminateChallenge = () => {
  return useMutation({
    mutationFn: async (matchData: { type: string; challengeId: string }) => {
      const client = await authorizedApiClient();
      const { data } = await client.post<ApiResponse<Match>>(
        "/challenge/terminate",
        matchData,
      );
      return data.data;
    },
    onSuccess: () => {
      // Optionally invalidate any queries related to matches or perform other actions
      queryClient.invalidateQueries({ queryKey: ["challenges"] });
    },
  });
};

// 29. List Challenge
export const useChallengeList = (
  userId: number | undefined,
  filter: string,
) => {
  return useInfiniteQuery({
    queryKey: ["challenges", filter, userId],
    queryFn: async ({ pageParam = "" }) => {
      const { data } = await apiClient.get<ApiResponse<ChallengeListResponse>>(
        `/challenge/list?userId=${userId}&filter=${filter}&cursor=${pageParam}`,
      );
      return {
        challenges: data.data,
        nextCursor: data.pagination?.nextCursor,
        hasMore: data.pagination?.hasMore || false,
      };
    },
    initialPageParam: "",
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    refetchInterval: 2500, // 2.5s
    enabled: userId != null,
  });
};

// 30. Delete Squad
export const useDeleteSquad = () => {
  return useMutation({
    mutationFn: async ({ squadId }: { squadId: string }) => {
      const client = await authorizedApiClient();
      const { data } = await client.delete<ApiResponse<SquadListResponse>>(
        `/squad/delete?squadId=${squadId}`,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["squads"] });
    },
  });
};

// 31. Bulk Delete Squads
export const useDeleteSquads = () => {
  return useMutation({
    mutationFn: async ({ squadIds }: { squadIds: string[] }) => {
      const client = await authorizedApiClient();
      const { data } = await client.delete<ApiResponse<{ deleted: string[] }>>(
        "/squad/bulk-delete",
        { data: { squadIds } },
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["squads"] });
    },
  });
};

// 32. Daily Tournament
export const useTournamentDaily = () => {
  return useQuery({
    queryKey: ["tournament"],
    queryFn: async () => {
      const { data } =
        await apiClient.get<ApiResponse<TournamentDetailsResponse>>(
          `/tournament/daily`,
        );
      return data.data;
    },
    refetchInterval: 2500, // 2.5s
  });
};

// 32. Shop
export const useShop = ({
  category,
  userId,
  limit,
  offset,
}: {
  category?: string;
  userId?: number;
  limit?: number;
  offset?: number;
} = {}) => {
  return useQuery({
    queryKey: ["shopList", category, userId, limit, offset],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiResponse<ShopListResponse>>(
        "/shop/list",
        {
          params: {
            category,
            userId,
            limit,
            offset,
          },
        },
      );
      return data.data.items;
    },
  });
};

// 32b. Shop - Request Purchase
export const useRequestPurchase = () => {
  const { signAndBroadcast } = useGameSigner();
  return useMutation({
    mutationFn: async (shopItemId: string) => {
      const client = await authorizedApiClient();
      const { data } = await client.post<
        ApiResponse<{
          transaction: string;
          shopItemId: string;
          quantity: number;
          pricePerUnit: number;
          totalPrice: number;
        }>
      >("/shop/request-purchase", { shopItemId });
      const { transaction, ...rest } = data.data;
      const signature = await signAndBroadcast(transaction);
      return { signature, ...rest };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stats"] });
      queryClient.invalidateQueries({ queryKey: ["solana.getBalance"] });
      queryClient.invalidateQueries({ queryKey: ["shopList"] });
    },
  });
};

// 32c. Inventory
export const useInventory = ({
  userId,
  limit,
  offset,
  cursor,
}: {
  userId: number | undefined;
  limit?: number;
  offset?: number;
  cursor?: string;
}) => {
  return useInfiniteQuery({
    queryKey: ["inventory", userId, limit, offset],
    queryFn: async ({ pageParam = cursor ?? "" }) => {
      const params: Record<string, string | number | undefined> = {
        userId,
      };

      if (limit !== undefined && limit !== null) {
        params.limit = limit;
      }

      if (offset !== undefined && offset !== null) {
        params.offset = offset;
      }

      if (pageParam) {
        params.cursor = pageParam;
      }

      const { data } = await apiClient.get<ApiResponse<InventoryListResponse>>(
        "/item/mine",
        {
          params,
        },
      );
      return {
        items: data.data.items,
        rarity: data.data.rarity,
        pagination: data.data.pagination,
        nextCursor: data.data.pagination.nextCursor,
        hasMore: data.data.pagination.hasMore,
      };
    },
    initialPageParam: "",
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: userId != null,
  });
};

// 32d. Use Item
export const useItem = () => {
  return useMutation({
    mutationFn: async (itemId: string) => {
      const client = await authorizedApiClient();
      const { data } = await client.post<ApiResponse<{ message: string }>>(
        "/item/use",
        { itemId },
      );
      return data.data;
    },
    onSuccess: () => {
      // Invalidate inventory after using an item
      queryClient.invalidateQueries({ queryKey: ["inventory"] });
      queryClient.invalidateQueries({ queryKey: ["stats"] });
    },
  });
};

// 33. Roulette
export const useRoulette = () => {
  return useMutation({
    mutationFn: async () => {
      const client = await authorizedApiClient();
      const { data } =
        await client.post<ApiResponse<RouletteResponse>>("/roulette/spin");
      return data.data;
    },
    onSuccess: () => {
      // Invalidate relevant queries when a spinning next item
      queryClient.invalidateQueries({ queryKey: ["stats"] });
    },
  });
};

// 39. Tournament Schedule
export const useTournamentSchedule = ({
  epoch,
  loaded = false,
}: {
  epoch: number;
  loaded: boolean;
}) => {
  return useQuery({
    enabled: loaded,
    queryKey: ["tournamentSchedule", epoch],
    queryFn: async () => {
      const { data } = await apiClient.get<
        ApiResponse<TournamentScheduleResponse>
      >(`/tournament/schedule?epoch=${epoch}`);
      return data.data;
    },
    refetchOnMount: true,
  });
};

// 40. Tournament Pointer
export const useTournamentPointer = () => {
  return useQuery({
    queryKey: ["tournamentPointer"],
    queryFn: async () => {
      const { data } =
        await apiClient.get<ApiResponse<TournamentDetailsResponse | null>>(
          `/tournament/pointer`,
        );
      return data.data;
    },
    staleTime: 1000 * 60 * 60, // 60 minutes
    refetchOnMount: true,
  });
};

// 41. Request Join Tournament
export const useRequestJoinTournament = () => {
  const { signAndBroadcast } = useGameSigner();
  return useMutation({
    mutationFn: async (body: {
      tournamentId: string;
      squadId: string;
      matchType: "long" | "short";
      // isFreePass: boolean;
      slotCount: number;
      password?: string;
    }) => {
      const client = await authorizedApiClient();
      const payload = {
        ...body,
        ...(body.password && { password: body.password.toLowerCase() }),
      };
      const { data } = await client.post<ApiResponse<string>>(
        "/tournament/request-join",
        payload,
      );
      const base64Tx = data.data;
      const signature = await signAndBroadcast(base64Tx);
      return signature;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stats"] });
      queryClient.invalidateQueries({ queryKey: ["solana.getBalance"] });
    },
  });
};

// 41b. Request Join Free Tournament
export const useRequestJoinFreeTournament = () => {
  return useMutation({
    mutationFn: async (body: {
      tournamentId: string;
      squadId: string;
      matchType: "long" | "short";
      slotCount: number;
      password?: string;
    }) => {
      const client = await authorizedApiClient();
      const payload = {
        ...body,
        ...(body.password && { password: body.password.toLowerCase() }),
      };
      const { data } = await client.post<ApiResponse<RequestJoinFreeResponse>>(
        "/tournament/request-join-free",
        payload,
      );
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stats"] });
      queryClient.invalidateQueries({ queryKey: ["solana.getBalance"] });
    },
  });
};

// 41c. Preview Sponsored Match
export const usePreviewSponsoredMatch = () => {
  return useMutation({
    mutationFn: async () => {
      const client = await authorizedApiClient();
      const { data } = await client.post<
        ApiResponse<PreviewSponsoredMatchResponse>
      >("/tournament/preview-sponsored-match", {});
      return data.data;
    },
  });
};

// 42. Leaderboard Referral
export const useLeaderboardReferralList = () => {
  return useQuery({
    queryKey: ["leaderboard-referral"],
    queryFn: async () => {
      const { data } = await apiClient.get<
        ApiResponse<LeaderboardReferralResponse>
      >(`/leaderboard/referral-leaderboard-by-points?limit=50`);
      return data.data;
    },
  });
};

// 43. Tournament Claim Share Bonus
export const useClaimShareBonus = () => {
  return useMutation({
    mutationFn: async (claimData: { tournamentId: string; skip?: boolean }) => {
      const client = await authorizedApiClient();
      const { data } = await client.post<ApiResponse<{ message: string }>>(
        "/tournament/claim-share-bonus",
        claimData,
      );
      return data.data;
    },
    onSuccess: () => {},
  });
};

// 44. Share endpoints (hash from AvailableTournamentToShare.shareHash)
export const getShareImageUrl = (hash: string) =>
  `${configs.API_BASE_URL}/share/${hash}/image`;

export const useShareHash = (hash: string) => {
  return useQuery({
    queryKey: ["share", hash],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiResponse<{ hash: string }>>(
        `/share/${hash}/`,
      );
      return data.data;
    },
    enabled: !!hash,
  });
};

export const useShareClaim = () => {
  return useMutation({
    mutationFn: async ({
      hash,
      proof_url,
    }: {
      hash: string;
      proof_url: string;
    }) => {
      const client = await authorizedApiClient();
      const { data } = await client.post<ApiResponse<{ message: string }>>(
        `/share/${hash}/claim`,
        { proof_url },
      );
      return data.data;
    },
  });
};

export const useShareXOAuthStatus = ({
  enabled = true,
}: {
  enabled?: boolean;
} = {}) => {
  return useQuery({
    queryKey: ["shareXOAuthStatus"],
    queryFn: async () => {
      const client = await authorizedApiClient();
      const { data } = await client.get<
        ApiResponse<ShareXOAuthStatusResponse>
      >("/share/x/oauth-status");
      return data.data;
    },
    enabled,
  });
};

export const useSubmitShareXOAuthToken = () => {
  return useMutation({
    mutationFn: async (tokenData: ShareXOAuthTokenRequest) => {
      const client = await authorizedApiClient();
      const { data } = await client.post<ApiResponse<{ message: string }>>(
        "/share/x/oauth-token",
        tokenData,
      );
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shareXOAuthStatus"] });
    },
  });
};

export const useSharePostAndClaim = () => {
  return useMutation({
    mutationFn: async ({ hash }: { hash: string }) => {
      const client = await authorizedApiClient();
      const { data } = await client.post<ApiResponse<SharePostAndClaimResponse>>(
        `/share/${hash}/post-and-claim`,
      );
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shareMissions"] });
      queryClient.invalidateQueries({ queryKey: ["stats"] });
      queryClient.invalidateQueries({ queryKey: ["solana.getBalance"] });
      queryClient.invalidateQueries({ queryKey: ["solana.getUsdcBalance"] });
    },
  });
};

// OneSignal Notifications
export const useScheduleNotification = () => {
  return useMutation({
    mutationFn: async (notificationData: {
      userAddress: string;
      title?: string;
      message: string;
      url?: string;
      sendAfter: string;
      ttl?: number;
    }) => {
      const client = await authorizedApiClient();
      const { data } = await client.post<ApiResponse<{ message: string }>>(
        "/onesignal/schedule",
        notificationData,
      );
      return data.data;
    },
  });
};

export const useSendNotification = () => {
  return useMutation({
    mutationFn: async (notificationData: {
      userAddress: string;
      title?: string;
      message: string;
      url?: string;
      ttl?: number;
    }) => {
      const client = await authorizedApiClient();
      const { data } = await client.post<ApiResponse<{ message: string }>>(
        "/onesignal/send",
        notificationData,
      );
      return data.data;
    },
  });
};

// World Mini App Notifications
export const useScheduleWorldNotification = () => {
  return useMutation({
    mutationFn: async (notificationData: {
      walletAddress: string;
      title: string;
      message: string;
      miniAppPath?: string;
      sendAfter: string;
    }) => {
      const client = await authorizedApiClient();
      const { data } = await client.post<ApiResponse<{ message: string }>>(
        "/world-notification/schedule",
        notificationData,
      );
      return data.data;
    },
  });
};

// Tournament Consecutive
export const useTournamentConsecutive = (options: {
  refetchInterval?: number | false;
  type: "rush" | "grand_rush" | "rush_rookie";
}) => {
  return useQuery({
    queryKey: ["useTournamentConsecutive", options.type],
    queryFn: async () => {
      const { data } = await apiClient.get<
        ApiResponse<TournamentDetailsResponse | null>
      >(`/tournament/latest-consecutive`, {
        params: {
          type: options.type,
        },
      });
      return data.data;
    },
    refetchInterval: options.refetchInterval ?? 2500,
    refetchOnMount: true,
  });
};

// Upload image
export const useUploadImage = () => {
  return useMutation({
    mutationFn: async (file: File) => {
      const client = await authorizedApiClient();

      const formData = new FormData();
      formData.append("profilePicture", file);

      const { data } = await client.post<ApiResponse<{ message: string }>>(
        "/user/upload-profile-picture",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      return data.data;
    },
    onSuccess: () => {},
  });
};

// Claim Legend Border
export const useClaimLegendBorder = () => {
  return useMutation({
    mutationFn: async () => {
      const client = await authorizedApiClient();
      const { data } = await client.post<ApiResponse<{ message: string }>>(
        "/reward/claim-legend-frame",
      );
      return data.data;
    },
    onSuccess: () => {
      // Invalidate relevant queries when claim
      queryClient.invalidateQueries({ queryKey: ["stats"] });
    },
  });
};

// Request Auto Join
export const useAutoJoin = () => {
  const { signAndBroadcast } = useGameSigner();
  return useMutation({
    mutationFn: async (autoJoinData: {
      squadId: string;
      matchType: string;
      roundCount: number;
      slotsPerRound: number; // 1
      totalUsdcStaked: number;
      rushType: "rush" | "grand_rush" | "rush_rookie";
    }) => {
      const client = await authorizedApiClient();
      const { data } = await client.post<
        ApiResponse<{
          transaction: string;
          squadId: string;
          squadType: string;
          roundCount: number;
          slotsPerRound: number;
          totalUsdcStaked: number;
        }>
      >("/auto-join/request", autoJoinData);
      const { transaction, ...rest } = data.data;
      const signature = await signAndBroadcast(transaction);
      return { signature, ...rest };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["queue"] });
      queryClient.invalidateQueries({ queryKey: ["stats"] });
      queryClient.invalidateQueries({ queryKey: ["solana.getBalance"] });
    },
  });
};

// Get queue list
export const useQueueList = (
  userId: number | undefined,
  rushType: string,
  options?: {
    refetchInterval?: number | false;
  },
) => {
  return useQuery({
    queryKey: ["queue", userId, rushType],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiResponse<QueueListResponse>>(
        `/auto-join/queue?userId=${userId}&rushType=${rushType}`,
      );
      return data.data;
    },
    enabled: userId != null,
    refetchInterval: options?.refetchInterval ?? 60_0000,
    refetchOnMount: true,
  });
};

export const useStatsSummary = (userId: number | undefined) => {
  return useQuery({
    queryKey: ["stats-summary", userId],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiResponse<StatsSummaryResponse>>(
        `/user/stats-summary?userId=${userId}`,
      );
      return data.data;
    },
    enabled: userId != null,
  });
};

export const useRenameSquad = () => {
  return useMutation({
    mutationFn: async (squadData: {
      squadId: string;
      newSquadName: string;
    }) => {
      const client = await authorizedApiClient();
      const { data } = await client.post<ApiResponse<{ message: string }>>(
        "/squad/rename",
        squadData,
      );
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["squads"] });
    },
  });
};

// 44. Next Available Tournament
export const useNextAvailableTournament = () => {
  return useQuery({
    queryKey: ["nextAvailableTournament"],
    queryFn: async () => {
      const { data } = await apiClient.get<
        ApiResponse<{
          tournamentId: string;
          startDate: number;
          dateEpoch: number;
          index: number;
        }>
      >("/tournament/next-available");
      return data.data;
    },
  });
};

export const useWinnerTakesAllList = () => {
  return useQuery({
    queryKey: ["winner-takes-all"],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiResponse<TournamentListResponse>>(
        `/tournament/list-winner-takes-all`,
      );
      return data.data;
    },
    refetchInterval: 2500,
    refetchOnMount: true,
  });
};

// 45. KOTH - Current
export const useKothCurrent = (options?: {
  refetchInterval?: number | false;
  kothType?: number;
}) => {
  const kothType = options?.kothType;
  return useQuery({
    queryKey: ["koth", "current", kothType],
    queryFn: async () => {
      const params = kothType != null ? `?kothType=${kothType}` : "";
      const { data } = await apiClient.get<
        ApiResponse<import("../types/api").KothCurrentResponse>
      >(`/koth/current${params}`);
      return data.data;
    },
    refetchOnMount: true,
    refetchInterval: options?.refetchInterval ?? 2500,
  });
};

// 46. KOTH - Details
export const useKothDetails = (kothId: string | undefined) => {
  return useQuery({
    queryKey: ["koth", "details", kothId],
    queryFn: async () => {
      const { data } = await apiClient.get<
        ApiResponse<import("../types/api").KothCurrentResponse>
      >(`/koth/details?kothId=${kothId}`);
      return data.data;
    },
    enabled: !!kothId,
    refetchOnMount: true,
    refetchInterval: 2500,
  });
};

// 47. KOTH - Request Create
export const useKothRequestCreate = () => {
  const { signAndBroadcast } = useGameSigner();
  return useMutation({
    mutationFn: async (payload: unknown) => {
      const client = await authorizedApiClient();
      const { data } = await client.post<
        ApiResponse<{ base64Tx: string; [key: string]: unknown }>
      >(`/koth/request-create`, payload);
      const signature = await signAndBroadcast(data.data.base64Tx);
      return signature;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["koth", "current"] });
      queryClient.invalidateQueries({ queryKey: ["stats"] });
      queryClient.invalidateQueries({ queryKey: ["solana.getBalance"] });
    },
  });
};

// 48. KOTH - Request Challenge
export const useKothRequestChallenge = () => {
  const { signAndBroadcast } = useGameSigner();
  return useMutation({
    mutationFn: async (payload: unknown) => {
      const client = await authorizedApiClient();
      const { data } = await client.post<
        ApiResponse<{ base64Tx: string; [key: string]: unknown }>
      >(`/koth/request-challenge`, payload);
      const signature = await signAndBroadcast(data.data.base64Tx);
      return signature;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["koth", "current"] });
      queryClient.invalidateQueries({ queryKey: ["stats"] });
      queryClient.invalidateQueries({ queryKey: ["solana.getBalance"] });
    },
  });
};

// 49. KOTH - King Edit Squad
export const useKothKingEditSquad = () => {
  return useMutation({
    mutationFn: async (payload: {
      kothSeed: string;
      newSquad: string;
      newSquadMatchType: "long" | "short";
    }) => {
      const client = await authorizedApiClient();
      const response = await client.post<ApiResponse<string>>(
        `/koth/king-edit-squad`,
        payload,
      );
      // Log full response for debugging
      console.log("Full API response:", response);
      console.log("Response data:", response.data);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["koth"] });
      queryClient.invalidateQueries({ queryKey: ["squads"] });
    },
  });
};

// 50. KOTH - Request Cashout (server-side, no client signing needed)
export const useKothRequestCashout = () => {
  return useMutation({
    mutationFn: async (payload: { kothSeed: string }) => {
      const client = await authorizedApiClient();
      const { data } = await client.post<
        ApiResponse<{
          txSig: string;
          kothSeed: string;
          totalPayout: number;
          basePrize: number;
          streakBonus: number;
          finalStreak: number;
        }>
      >(`/koth/request-cashout`, payload);
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["koth"] });
      queryClient.invalidateQueries({ queryKey: ["stats"] });
      queryClient.invalidateQueries({ queryKey: ["solana.getBalance"] });
    },
  });
};

// 51. KOTH - History (Infinite/Cursor)
export const useKothHistoryInfinite = (userId: number | undefined) => {
  return useInfiniteQuery({
    queryKey: ["kothHistory", userId],
    queryFn: async ({ pageParam = "" }) => {
      const ts = new Date().getTime();
      const cursorParam = pageParam
        ? `&cursor=${encodeURIComponent(String(pageParam))}`
        : "";
      const { data } = await apiClient.get<ApiResponse<KothHistoryItem[]>>(
        `/koth/history?userId=${userId}${cursorParam}&_t=${ts}`,
      );
      return {
        koths: data.data,
        nextCursor: data.pagination?.nextCursor ?? undefined,
        hasMore: data.pagination?.hasMore || false,
      };
    },
    initialPageParam: "",
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: !!userId,
  });
};

// 51b. KOTH - Global History (Infinite/Cursor)
export const useKothGlobalHistoryInfinite = (kothType?: 0 | 1) => {
  return useInfiniteQuery({
    queryKey: ["kothHistory", "global", kothType],
    queryFn: async ({ pageParam = "" }) => {
      const ts = new Date().getTime();
      const params = new URLSearchParams();
      if (pageParam) params.set("cursor", String(pageParam));
      if (kothType != null) params.set("kothType", String(kothType));
      params.set("_t", String(ts));
      const { data } = await apiClient.get<ApiResponse<KothHistoryItem[]>>(
        `/koth/history?${params.toString()}`,
      );
      return {
        koths: data.data,
        nextCursor: data.pagination?.nextCursor ?? undefined,
        hasMore: data.pagination?.hasMore || false,
      };
    },
    initialPageParam: "",
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  });
};

// 52. Claim Welcome Pack
export const useClaimWelcomePack = () => {
  return useMutation({
    mutationFn: async () => {
      const client = await authorizedApiClient();
      const { data } = await client.post<ApiResponse<string>>(
        `/user/claim-welcome-pack`,
        { rewardType: "welcome-pack" },
      );
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stats"] });
    },
  });
};

export const useWTAHistoryGlobal = (page = 1, limit = 30) => {
  return useQuery({
    queryKey: ["wtaTournamentHistory", page, limit],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiResponse<Tournament[]>>(
        `/tournament/history?type=wta&page=${page}&limit=${limit}`,
      );
      return data.data;
    },
  });
};

// Rush / Grand Rush History
export const useRushHistoryGlobal = (
  type: "rush" | "rush_rookie" | "grand_rush",
  page = 1,
  limit = 30,
) => {
  return useQuery({
    queryKey: ["rushTournamentHistory", type, page, limit],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiResponse<Tournament[]>>(
        `/tournament/history?type=${type}&page=${page}&limit=${limit}`,
      );
      return data.data;
    },
  });
};

// 53. Squad Performance
export const useSquadPerformance = (
  playerIds: number[],
  squadType: "long" | "short",
  enabled = true,
  options?: { refetchInterval?: number | false },
) => {
  return useQuery({
    queryKey: ["squadPerformance", playerIds, squadType],
    queryFn: async () => {
      const { data } = await apiClient.post<
        ApiResponse<SquadPerformanceResponse>
      >("/squad/performance", { playerIds: playerIds, squadType: squadType });
      return data.data;
    },
    enabled:
      Array.isArray(playerIds) &&
      playerIds.length > 0 &&
      Boolean(squadType) &&
      enabled,
    refetchInterval: options?.refetchInterval ?? false,
    refetchIntervalInBackground: false,
  });
};

export const useLeaderboardGlobal = (tier?: number) => {
  return useInfiniteQuery({
    queryKey: ["leaderboardGlobal", tier],
    queryFn: async ({ pageParam = "" }) => {
      const params = new URLSearchParams();
      if (pageParam) params.set("cursor", String(pageParam));
      if (tier !== undefined) params.set("tier", String(tier));
      const { data } = await apiClient.get<ApiResponse<LeaderboardResponse>>(
        `/leaderboard/all-time?${params.toString()}`,
      );
      return {
        leaderboard: data.data,
        nextCursor: data.pagination?.nextCursor,
        hasMore: data.pagination?.hasMore || false,
      };
    },
    initialPageParam: "",
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });
};

export const useLeaderboardSeasonal = (seasonIndex?: number, tier?: number) => {
  return useInfiniteQuery({
    queryKey: ["leaderboardSeasonal", seasonIndex, tier],
    queryFn: async ({ pageParam = "" }) => {
      const params = new URLSearchParams();
      if (seasonIndex !== undefined)
        params.set("seasonIndex", String(seasonIndex));
      if (pageParam) params.set("cursor", String(pageParam));
      if (tier !== undefined) params.set("tier", String(tier));
      const { data } = await apiClient.get<SeasonalLeaderboardResponse>(
        `/leaderboard/seasonal?${params.toString()}`,
      );
      return {
        leaderboard: data.data,
        season: data.season,
        nextCursor: data.pagination?.nextCursor,
        hasMore: data.pagination?.hasMore || false,
      };
    },
    initialPageParam: "",
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });
};

// 54. Rush Leaderboard
export const useRushLeaderboard = ({
  epoch,
  loaded = false,
  tier,
}: {
  epoch: number;
  loaded?: boolean;
  tier?: number;
}) => {
  return useQuery({
    enabled: loaded,
    queryKey: ["rushLeaderboard", epoch, tier],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set("epoch", String(epoch));
      if (tier !== undefined) params.set("tier", String(tier));
      const { data } = await apiClient.get<
        ApiResponse<RushLeaderboardResponse>
      >(`/leaderboard/rush?${params.toString()}`);
      return data.data;
    },
    refetchOnMount: true,
  });
};

// 54b. Grand Rush Leaderboard
export const useGrandRushLeaderboard = ({
  epoch,
  loaded = false,
}: {
  epoch: number;
  loaded?: boolean;
}) => {
  return useQuery({
    enabled: loaded,
    queryKey: ["grandRushLeaderboard", epoch],
    queryFn: async () => {
      const { data } = await apiClient.get<
        ApiResponse<RushLeaderboardResponse>
      >(`/leaderboard/grand-rush?epoch=${epoch}`);
      return data.data;
    },
    refetchOnMount: true,
  });
};

// 54c. Daily MMR Leaderboard
// Mirrors the rush/koth shape — omit `epoch` for today's live ranking,
// pass an epoch in the past for that day's snapshot.
export const useDailyMmrLeaderboard = ({
  epoch,
  page = 1,
  limit = 30,
  enabled = true,
}: {
  epoch?: number;
  page?: number;
  limit?: number;
  enabled?: boolean;
} = {}) => {
  return useQuery({
    enabled,
    queryKey: ["dailyMmrLeaderboard", epoch ?? "live", page, limit],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set("limit", String(limit));
      params.set("page", String(page));
      if (epoch !== undefined) params.set("epoch", String(epoch));
      const { data } = await apiClient.get<
        ApiResponse<DailyMmrLeaderboardResponse>
      >(`/leaderboard/daily-mmr?${params.toString()}`);
      return data.data;
    },
    refetchOnMount: true,
  });
};

// 55. Change Log
export const useChangeLog = () => {
  return useQuery({
    queryKey: ["changeLog"],
    queryFn: async () => {
      const { data } =
        await apiClient.get<ApiResponse<ChangeLogResponse[]>>(`/changelogs`);
      return data.data;
    },
  });
};

// 56. Create Hosted Tournament
export const useCreateHostedTournament = () => {
  const { signAndBroadcast } = useGameSigner();
  return useMutation({
    mutationFn: async (tournamentData: {
      squadId: string;
      matchType: string;
      slotCount: number;
      appOrigin: string;
      maxPlayer: number;
      entryAmountOnUsdc: number;
      name: string;
      startDate: number;
      duration: number;
      maxMultiEntryCount: number;
      topWinner: number;
      password?: string;
    }) => {
      const client = await authorizedApiClient();
      const payload = {
        ...tournamentData,
        ...(tournamentData.password && {
          password: tournamentData.password.toLowerCase(),
        }),
      };
      const { data } = await client.post<
        ApiResponse<{ base64Tx: string; tournamentId: string }>
      >("/tournament/request-create-user-hosted-tournament", payload);
      const { base64Tx, tournamentId } = data.data;
      const signature = await signAndBroadcast(base64Tx);
      return { signature, tournamentId };
    },
    onSuccess: () => {
      // Optionally invalidate any queries related to squads or perform other actions
      queryClient.invalidateQueries({ queryKey: ["hostedTournament"] });
    },
  });
};

// 56b. View Tournament Password (owner only)
export const useViewTournamentPassword = () => {
  return useMutation({
    mutationFn: async (tournamentId: string) => {
      const client = await authorizedApiClient();
      const { data } = await client.post<ApiResponse<{ password: string }>>(
        "/tournament/password",
        { tournamentId },
      );
      return data.data;
    },
  });
};

// 56c. Verify Tournament Password
export const useVerifyTournamentPassword = () => {
  return useMutation({
    mutationFn: async ({
      tournamentId,
      password,
    }: {
      tournamentId: string;
      password: string;
    }) => {
      const client = await authorizedApiClient();
      const { data } = await client.post<ApiResponse<{ isCorrect: boolean }>>(
        "/tournament/check-password",
        { tournamentId, password: password.toLowerCase() },
      );
      if (!data.data?.isCorrect) {
        throw new Error("Incorrect password");
      }
      return data.data;
    },
  });
};

// 57. Hosted Tournament List
export const useUserHostedTournamentList = ({
  searchBy,
  filterBy,
  gameMode,
  min,
  max,
  cursor,
  limit = 20,
  userId,
}: {
  searchBy?: string;
  filterBy?: string;
  gameMode?: "championship" | "tournament";
  min?: string | number;
  max?: string | number;
  cursor?: string;
  limit?: number;
  userId?: number;
} = {}) => {
  return useInfiniteQuery({
    queryKey: [
      "userHostedTournamentList",
      searchBy,
      filterBy,
      gameMode,
      min,
      max,
      limit,
      userId,
    ],
    queryFn: async ({ pageParam = cursor ?? "" }) => {
      const { data } = await apiClient.get<ApiResponse<TournamentListResponse>>(
        "/tournament/list-user-hosted",
        {
          params: {
            searchBy,
            filterBy,
            gameMode,
            min,
            max,
            limit,
            cursor: pageParam || undefined,
            userId,
          },
        },
      );

      return {
        tournaments: data.data,
        nextCursor: data.pagination?.nextCursor ?? null,
        hasMore: data.pagination?.hasMore ?? false,
      };
    },
    initialPageParam: cursor ?? "",
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });
};

// 58. Tournament - Edit Squad
export const useTournamentEditSquad = () => {
  return useMutation({
    mutationFn: async (payload: {
      tournamentId: string;
      squadId: string;
      newSquadId: string;
      newSquadMatchType: "long" | "short";
    }) => {
      const client = await authorizedApiClient();
      const response = await client.post<
        ApiResponse<{
          tournamentId: string;
          squadId: string;
          newSquadId: string;
          newSquadMatchType: "long" | "short";
        }>
      >(`/tournament/edit-squad`, payload);
      return response.data.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["tournament", variables.tournamentId],
      });
      queryClient.invalidateQueries({ queryKey: ["squads"] });
    },
  });
};

export const useKothLeaderboard = ({
  epoch,
  by = "streak",
  loaded = false,
  tier,
}: {
  epoch: number;
  by?: "streak" | "trophy";
  loaded?: boolean;
  tier?: number;
}) => {
  return useQuery({
    enabled: loaded,
    queryKey: ["rushKoth", epoch, by, tier],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set("epoch", String(epoch));
      params.set("by", by);
      if (tier !== undefined) params.set("tier", String(tier));
      const { data } = await apiClient.get<
        ApiResponse<KothLeaderboardResponse>
      >(`/leaderboard/koth?${params.toString()}`);
      return data.data;
    },
    refetchOnMount: true,
  });
};

// 59. Championship List
export const useChampionshipList = ({
  filterByRound,
  filterByStatus,
  cursor,
  limit = 100,
}: {
  filterByRound?: number;
  filterByStatus?: "all" | "waiting" | "live" | "completed" | "canceled";
  cursor?: string;
  limit?: number;
}) => {
  return useInfiniteQuery({
    queryKey: ["championshipList", filterByRound, limit],
    queryFn: async ({ pageParam = cursor ?? "" }) => {
      const params: Record<string, string | number | undefined> = {
        limit,
      };

      if (filterByRound !== undefined && filterByRound !== null) {
        params.filterByRound = filterByRound;
      }

      if (filterByStatus !== undefined && filterByStatus !== null) {
        params.filterByStatus = filterByStatus;
      }

      if (pageParam) {
        params.cursor = pageParam;
      }

      const { data } = await apiClient.get<ChampionshipListApiResponse>(
        "/championship/list",
        {
          params,
        },
      );

      return {
        championships: data.data,
        rounds: data.rounds,
        nextCursor: data.pagination.nextCursor,
        hasMore: data.pagination.hasMore,
      };
    },
    initialPageParam: cursor ?? "",
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });
};

// 60. Championship Details
export const useChampionshipDetails = (
  tournamentId: string,
  options?: { refetchInterval?: number | false },
) => {
  return useQuery({
    queryKey: ["championship", tournamentId],
    queryFn: async () => {
      const { data } = await apiClient.get<
        ApiResponse<ChampionshipDetailsResponse>
      >(`/championship/detail?tournamentId=${tournamentId}`);
      return data.data;
    },
    enabled: !!tournamentId,
    refetchInterval: options?.refetchInterval ?? 2500,
    refetchOnMount: true,
  });
};

// 62. Championship - Edit Squad
export const useChampionshipEditSquad = () => {
  return useMutation({
    mutationFn: async (payload: {
      championshipMatchId: string;
      squadId: string;
      newSquadId: string;
      newSquadMatchType: "long" | "short";
    }) => {
      const client = await authorizedApiClient();
      const response = await client.post<
        ApiResponse<{
          championshipMatchId: string;
          squadId: string;
          newSquadId: string;
          newSquadMatchType: "long" | "short";
        }>
      >(`/championship/edit-squad`, payload);
      return response.data.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["championship", variables.championshipMatchId],
      });
      queryClient.invalidateQueries({ queryKey: ["squads"] });
    },
  });
};

// 63. Referral Claim
export const useReferralClaim = () => {
  const { signAndBroadcast } = useGameSigner();
  return useMutation({
    mutationFn: async () => {
      const client = await authorizedApiClient();
      const { data } = await client.post<ApiResponse<{ solTx: string }>>(
        "/claim/withdraw-referral-and-cashback",
      );
      const signature = await signAndBroadcast(data.data.solTx);
      return signature;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["referral"] });
      queryClient.invalidateQueries({ queryKey: ["leaderboard-referral"] });
      queryClient.invalidateQueries({ queryKey: ["stats"] });
      queryClient.invalidateQueries({ queryKey: ["solana.getBalance"] });
    },
  });
};

// 63b. USDC Bonus Claim
export const useClaimUsdcBonus = () => {
  return useMutation({
    mutationFn: async () => {
      const client = await authorizedApiClient();
      const { data } = await client.post<
        ApiResponse<{
          withdrawalId: string;
          amountUsdc: number;
          txSignature: string;
        }>
      >("/claim/match-bonus");
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["referral"] });
      queryClient.invalidateQueries({ queryKey: ["stats"] });
    },
  });
};

// 64. Gacha Prize List
export const useGachaPrize = () => {
  return useQuery({
    queryKey: ["gacha-prize"],
    queryFn: async () => {
      const { data } =
        await apiClient.get<ApiResponse<GachaPrizeResponse>>("/gacha/prizes");
      return data.data;
    },
  });
};

// 65. Claim Gacha
export const useClaimGacha = () => {
  return useMutation({
    mutationFn: async (gachaData: { gachaType: number; count: number }) => {
      const client = await authorizedApiClient();
      const { data } = await client.post<ApiResponse<GachaClaimResponse>>(
        "/gacha/claim",
        gachaData,
      );
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["inventory"] });
      queryClient.invalidateQueries({ queryKey: ["stats"] });
    },
  });
};

// 67. Claim Free Gacha Coin
export const useClaimFreeGachaCoin = () => {
  return useMutation({
    mutationFn: async () => {
      const client = await authorizedApiClient();
      const { data } =
        await client.post<ApiResponse<{ message: string }>>(
          "/claim/gacha-coin",
        );
      return data.data;
    },
    onSuccess: () => {
      // Invalidate inventory and stats after claiming free gacha coin
      queryClient.invalidateQueries({ queryKey: ["inventory"] });
      queryClient.invalidateQueries({ queryKey: ["stats"] });
    },
  });
};

// 68. Gacha History Leaderboard
export const useGachaHistory = () => {
  return useInfiniteQuery({
    queryKey: ["gacha-history"],
    queryFn: async ({ pageParam = 0 }) => {
      const { data } = await apiClient.get<ApiResponse<GachaHistoryItem[]>>(
        "/gacha/history",
        {
          params: {
            limit: 30,
            offset: pageParam,
          },
        },
      );
      return data;
    },
    getNextPageParam: (lastPage, allPages) => {
      const currentOffset = allPages.length * 30;
      if (lastPage?.data?.length < 30) return undefined;
      return currentOffset;
    },
    initialPageParam: 0,
  });
};

// 69. Item Details
export const useItemDetails = (userId: number | undefined, itemId: string) => {
  return useQuery({
    queryKey: ["itemDetails", userId, itemId],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiResponse<ItemDetailsResponse>>(
        "/item/detail",
        {
          params: {
            userId,
            itemId,
          },
        },
      );
      return data.data.itemDetails;
    },
    enabled: userId != null && !!itemId,
  });
};

// 70. Convert Free Pass to Lottery Tickets
export const useConvertFreePass = () => {
  return useMutation({
    mutationFn: async () => {
      const client = await authorizedApiClient();
      const { data } = await client.post<
        ApiResponse<{ ticketsEarned: number; remainingFreePass: number }>
      >("/claim/convert-free-pass");
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stats"] });
      queryClient.invalidateQueries({ queryKey: ["inventory"] });
    },
  });
};

// 71. Convert Gacha Coin to Lucky Tickets
export const useConvertGachaCoin = () => {
  return useMutation({
    mutationFn: async () => {
      const client = await authorizedApiClient();
      const { data } = await client.post<{
        success: boolean;
        result: { gachaCoinsUsed: number; luckyDrawTickets: number; gachaCoinsRemaining: number };
      }>("/claim/convert-gacha-coin");
      return data.result;
    },
  });
};

// 72. Lucky Draw Prizes
export const useLuckyDrawPrizes = (userId?: number) => {
  return useQuery({
    queryKey: ["lucky-draw-prizes", userId],
    queryFn: async () => {
      const { data } = await apiClient.get<
        ApiResponse<LuckyDrawPrizesResponse>
      >("/lucky-draw/prizes", {
        params: userId != null ? { userId } : undefined,
      });
      return data.data;
    },
  });
};

// 71.1 Lucky Draw Tickets
export const useLuckyDrawTickets = (userId?: number) => {
  return useQuery({
    queryKey: ["lucky-draw-tickets", userId],
    queryFn: async () => {
      const client = await authorizedApiClient();
      const { data } = await client.get<ApiResponse<LuckyDrawTicketsResponse>>(
        "/lucky-draw/tickets",
        {
          params: userId != null ? { userId } : undefined,
        },
      );
      return data.data;
    },
    enabled: userId != null,
  });
};

// 72. Lucky Draw Participate
export const useLuckyDrawParticipate = () => {
  return useMutation({
    mutationFn: async (payload: { ticketCount: number }) => {
      const client = await authorizedApiClient();
      const { data } = await client.post<
        ApiResponse<LuckyDrawParticipateResponse>
      >("/lucky-draw/participate", payload);
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["lucky-draw-prizes"] });
      queryClient.invalidateQueries({ queryKey: ["lucky-draw-tickets"] });
      queryClient.invalidateQueries({ queryKey: ["lucky-draw-leaderboard"] });
    },
  });
};

// 73. Lucky Draw History (Infinite/Cursor)
export const useLuckyDrawHistoryInfinite = ({
  limit = 30,
  cursor,
  offset,
}: {
  limit?: number;
  cursor?: string;
  offset?: number;
} = {}) => {
  return useInfiniteQuery({
    queryKey: ["lucky-draw-history", limit],
    queryFn: async ({ pageParam = cursor ?? offset ?? 0 }) => {
      const params = new URLSearchParams();
      if (limit) params.set("limit", String(limit));
      if (typeof pageParam === "number") {
        params.set("offset", String(pageParam));
      } else if (pageParam) {
        params.set("cursor", String(pageParam));
      }

      const query = params.toString();
      const endpoint = query
        ? `/lucky-draw/history/?${query}`
        : "/lucky-draw/history/";
      const { data } =
        await apiClient.get<ApiResponse<LuckyDrawHistoryResponse>>(endpoint);

      const payload = data.data;
      const nextCursor = payload.pagination?.nextCursor ?? null;
      return {
        draws: payload.draws ?? [],
        limit: payload.limit ?? limit,
        total: payload.total ?? 0,
        offset: payload.offset ?? 0,
        pagination: payload.pagination,
        nextCursor,
        hasMore: payload.pagination?.hasMore ?? false,
      };
    },
    initialPageParam: cursor ?? offset ?? 0,
    getNextPageParam: (lastPage) => {
      if (lastPage.nextCursor) return lastPage.nextCursor;
      if (!lastPage.hasMore) return undefined;
      return lastPage.offset + lastPage.limit;
    },
  });
};

// 74. Lucky Draw History Detail
export const useLuckyDrawHistoryDetail = ({
  id,
  userId,
}: {
  id?: string;
  userId?: number;
} = {}) => {
  return useQuery({
    queryKey: ["lucky-draw-history-detail", id, userId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (id) params.set("id", id);
      if (userId != null) params.set("userId", String(userId));

      const { data } = await apiClient.get<
        ApiResponse<LuckyDrawHistoryDetails>
      >(`/lucky-draw/history/detail?${params.toString()}`);

      return data.data;
    },
    enabled: Boolean(id),
  });
};

// 75. Lucky Draw Detail By Date
export const useLuckyDrawDetailByDate = ({
  epoch,
  userId,
}: {
  epoch?: number;
  userId?: number;
} = {}) => {
  return useQuery({
    queryKey: ["lucky-draw-detail-by-date", epoch, userId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (epoch != null) params.set("epoch", String(epoch));
      if (userId != null) params.set("userId", String(userId));

      const { data } = await apiClient.get<
        ApiResponse<LuckyDrawDetailByDateResponse>
      >(`/lucky-draw/detail?${params.toString()}`);

      return data.data;
    },
    enabled: epoch != null && epoch > 0,
  });
};

// Lucky Draw Leaderboard (cursor-paginated)
export const useLuckyDrawLeaderboard = (userId?: number) => {
  return useInfiniteQuery({
    queryKey: ["lucky-draw-leaderboard", userId],
    queryFn: async ({ pageParam }) => {
      const params: Record<string, string | number> = {};
      if (userId != null) params.userId = userId;
      if (pageParam) params.cursor = pageParam;

      const { data } = await apiClient.get<
        ApiResponse<LuckyDrawLeaderboardResponse>
      >("/lucky-draw/leaderboard", { params });
      return data.data;
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.pagination?.hasMore ? lastPage.pagination.nextCursor : undefined,
  });
};

// Report Player
export const useReportPlayer = () => {
  return useMutation({
    mutationFn: async (body: SubmitReportRequest) => {
      const client = await authorizedApiClient();
      const { data } = await client.post<ApiResponse<SubmitReportResponse>>(
        "/report",
        body,
      );
      return data.data;
    },
    onSuccess: () => {
      // Refresh /report/status so the just-reported user flips to
      // canReport: false → "Reported" disabled state without a reload.
      queryClient.invalidateQueries({ queryKey: ["canReport"] });
    },
  });
};

// Report Status - batch check if reporter can report players (GET)
export const useCanReportBatch = (
  reportedIds: number[],
  reporterId: number | undefined,
) => {
  return useQuery({
    queryKey: ["canReport", reporterId, ...reportedIds],
    queryFn: async () => {
      const { data } = await apiClient.get<
        ApiResponse<CanReportPlayerStatusItem[]>
      >(
        `/report/status?reporterId=${reporterId}&reportedIds=${reportedIds.join(",")}`,
      );
      return data.data ?? [];
    },
    enabled: reportedIds.length > 0 && reporterId != null,
  });
};

// Report Token
export const useReportToken = () => {
  return useMutation({
    mutationFn: async (body: SubmitTokenReportRequest) => {
      const client = await authorizedApiClient();
      const { data } = await client.post<
        ApiResponse<SubmitTokenReportResponse>
      >("/report/token", body);
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["canReportToken"] });
    },
  });
};

// Report Token Status - batch check if user can report tokens (GET)
export const useCanReportTokenBatch = (
  pythLazerIds: number[],
  reporterId: number | undefined,
) => {
  return useQuery({
    queryKey: ["canReportToken", reporterId, ...pythLazerIds],
    queryFn: async () => {
      const { data } = await apiClient.get<
        ApiResponse<CanReportTokenStatusItem[]>
      >(
        `/report/token/status?reporterId=${reporterId}&pythLazerIds=${pythLazerIds.join(",")}`,
      );
      return data.data ?? [];
    },
    enabled: pythLazerIds.length > 0 && reporterId != null,
  });
};

// Share2Earn Missions
export const useShareMissions = (userId: number | undefined) => {
  return useQuery({
    queryKey: ["shareMissions", userId],
    queryFn: async () => {
      const { data } = await apiClient.get<
        ApiResponse<ShareMissionListResponse>
      >(`/share/missions?userId=${userId}`);
      return data.data;
    },
    enabled: userId != null,
  });
};

// 60b. Championship Bracket (user-hosted, GET /tournament/championship-bracket)
export const useChampionshipBracket = (
  tournamentId: string,
  options?: { refetchInterval?: number | false },
) => {
  return useQuery({
    queryKey: ["championshipBracket", tournamentId],
    queryFn: async () => {
      const { data } = await apiClient.get<
        ApiResponse<ChampionshipBracketResponse>
      >(`/tournament/championship-bracket?tournamentId=${tournamentId}`);
      return data.data;
    },
    enabled: Boolean(tournamentId),
    refetchInterval:
      options?.refetchInterval !== undefined
        ? options.refetchInterval
        : (query) => {
            const bracketData = query.state.data;
            if (!bracketData) return false;
            if (bracketData.tournament.tournamentStatus === "completed")
              return false;
            return 5000;
          },
  });
};

// 60c. Create User-Hosted Championship
export const useCreateUserHostedChampionship = () => {
  const { signAndBroadcast } = useGameSigner();
  return useMutation({
    mutationFn: async (payload: {
      name: string;
      maxPlayer: number;
      entryAmountOnUsdc: number;
      squadId: string;
      matchType: string;
      slotCount: number;
      startDate: number;
      appOrigin: string;
      topWinner: number;
      waitingDuration: number;
      liveDuration: number;
      payoutList: { payout: unknown[] };
      password?: string;
    }) => {
      const client = await authorizedApiClient();
      const { data } = await client.post<
        ApiResponse<{ base64Tx: string; tournamentId: string }>
      >("/tournament/request-create-user-hosted-championship", payload);
      const { base64Tx, tournamentId } = data.data;
      const signature = await signAndBroadcast(base64Tx);
      return { signature, tournamentId };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["userHostedTournamentList"] });
    },
  });
};

// 60d. Hosted Championship Match Detail (GET /tournament/championship-detail)
export const useHostedChampionshipDetail = (
  matchId: string,
  options?: { refetchInterval?: number | false },
) => {
  return useQuery({
    queryKey: ["hostedChampionshipDetail", matchId],
    queryFn: async () => {
      const { data } = await apiClient.get<
        ApiResponse<ChampionshipDetailsResponse>
      >(`/tournament/championship-detail?tournamentId=${matchId}`);
      return data.data;
    },
    enabled: Boolean(matchId),
    refetchInterval: (query) => {
      if (options?.refetchInterval === false) return false;
      if (options?.refetchInterval !== undefined)
        return options.refetchInterval;
      const data = query.state.data;
      if (data?.status === "completed") return false;
      return 2500;
    },
    refetchOnMount: true,
  });
};

// 60e. Edit Squad for Private Championship Match
export const useEditPrivateChampionshipSquad = () => {
  return useMutation({
    mutationFn: async (payload: {
      matchId: string;
      squadId: string;
      newSquadId: string;
      newSquadMatchType: "long" | "short";
    }) => {
      const client = await authorizedApiClient();
      const { data } = await client.post<
        ApiResponse<{ success: boolean; new_player_hash: string }>
      >("/tournament/edit-championship-squad", payload);
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["championshipBracket"] });
      queryClient.invalidateQueries({ queryKey: ["hostedChampionshipDetail"] });
      queryClient.invalidateQueries({ queryKey: ["squads"] });
    },
  });
};

// 61. Onboarding Missions
// 61a. Get Onboarding Missions
export const useOnboardingMissions = (userId?: number) => {
  return useQuery({
    queryKey: ["onboardingMissions", userId],
    queryFn: async () => {
      const params = userId != null ? `?userId=${userId}` : "";
      const { data } = await apiClient.get<
        ApiResponse<OnboardingMissionsResponse>
      >(`/onboarding/missions${params}`);
      return data.data;
    },
  });
};

// 61b. Claim Onboarding Mission
export const useClaimOnboardingMission = () => {
  return useMutation({
    mutationFn: async (missionId: string) => {
      const client = await authorizedApiClient();
      const { data } = await client.post<
        ApiResponse<OnboardingMissionsResponse>
      >("/onboarding/claim", { missionId });
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["onboardingMissions"] });
      queryClient.invalidateQueries({ queryKey: ["stats"] });
    },
  });
};

// 62. Weekly Bonus
export const useWeeklyBonus = (userPublicKey: string) => {
  return useQuery({
    queryKey: ["weeklyBonus", userPublicKey],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiResponse<WeeklyBonusResponse>>(
        `/mission/weekly-bonus?userPublicKey=${userPublicKey}`,
      );
      return data.data;
    },
    enabled: !!userPublicKey,
  });
};

// 62b. Claim Weekly Bonus
export const useClaimWeeklyBonus = () => {
  return useMutation({
    mutationFn: async () => {
      const client = await authorizedApiClient();
      const { data } = await client.post<ApiResponse<void>>(
        "/mission/weekly-bonus/claim",
      );
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["weeklyBonus"] });
      queryClient.invalidateQueries({ queryKey: ["stats"] });
    },
  });
};

// Streak Mission - GET
export const useStreakMission = (userId: number | undefined) => {
  return useQuery({
    queryKey: ["streakMission", userId],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiResponse<StreakMissionResponse>>(
        `/mission/streak?userId=${userId}`,
      );
      return data.data;
    },
    enabled: userId != null,
  });
};

// missions-v2: weekly-streak scope. Same envelope shape as milestone-streak
// (4-cycle window, `weeks` / `currentWeek` fields). Independent streak
// counter from milestone-streak — they coexist on separate FE pages.
export const useWeeklyStreakMission = (userId: number | undefined) => {
  return useQuery({
    queryKey: ["weeklyStreakMission", userId],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiResponse<WeeklyStreakResponse>>(
        `/mission?scope=weekly-streak&userId=${userId}`,
      );
      return data.data;
    },
    enabled: userId != null,
    staleTime: 30_000,
  });
};

// missions-v2: milestone-streak read endpoint. Per-user trophy-threshold
// missions, a per-cycle completionBonus, and a streakBonus across cycles.
// Resets weekly Mon 08:00 UTC (Mon 15:00 JKT).
export const useMilestoneStreakMission = (userId: number | undefined) => {
  return useQuery({
    queryKey: ["milestoneStreakMission", userId],
    queryFn: async () => {
      const { data } = await apiClient.get<
        ApiResponse<MilestoneStreakResponse>
      >(`/mission?scope=milestone-streak&userId=${userId}`);
      return data.data;
    },
    enabled: userId != null,
    staleTime: 30_000,
  });
};

// Streak Mission - Claim
export const useClaimStreakMission = () => {
  return useMutation({
    mutationFn: async (body: StreakMissionClaimRequest) => {
      const client = await authorizedApiClient();
      const { data } = await client.post<ApiResponse<{ message: string }>>(
        "/mission/streak/claim",
        body,
      );
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["streakMission"] });
    },
  });
};

// Promote user tier (Rookie → Pro)
export const usePromoteTier = () => {
  return useMutation({
    mutationFn: async () => {
      const client = await authorizedApiClient();
      const { data } =
        await client.post<
          ApiResponse<{ success: boolean; tier: number; message?: string }>
        >("/user/promote-tier");
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stats"] });
      queryClient.invalidateQueries({ queryKey: ["onboardingMissions"] });
    },
  });
};

// 62. Check X (Twitter) Account Eligibility
export const useCheckTwitterEligibility = () => {
  return useMutation({
    mutationFn: async (username: string) => {
      const client = await authorizedApiClient();
      const { data } = await client.get<
        ApiResponse<{
          eligible: boolean;
          reason?: string;
          accountAgeDays?: number;
          followers?: number;
        }>
      >("/user/check-x", {
        params: { username },
      });
      return data.data;
    },
  });
};

export const useHotspots = () => {
  return useQuery({
    queryKey: ["hotspots"],
    queryFn: async () => {
      const { data } =
        await apiClient.get<ApiResponse<HotspotEntry[]>>("/hotspot");
      return data.data;
    },
  });
};

// 63. My Squad In Match
export const useMySquadInMatch = (
  matchType: "tournament" | "koth" | "championship" | undefined,
  matchId: string | undefined,
) => {
  return useQuery({
    queryKey: ["mySquadInMatch", matchType, matchId],
    queryFn: async () => {
      const client = await authorizedApiClient();
      const { data } = await client.get<
        ApiResponse<import("../types/api").MySquadInMatchResponse>
      >(`/squad/my-squad-in-match?matchType=${matchType}&matchId=${matchId}`);
      return data.data;
    },
    enabled: !!matchType && !!matchId,
  });
};

// Event Calendar
export const useCalendarEvents = () => {
  return useQuery({
    queryKey: ["calendar-events"],
    queryFn: async () => {
      // Response is { events: [...] } directly — no ApiResponse wrapper
      const { data } =
        await apiClient.get<CalendarEventsResponse>("/events/calendar");
      return data.events;
    },
    staleTime: 5 * 60 * 1000,
  });
};

// Global theme / campaign data
export const useTheme = () => {
  return useQuery({
    queryKey: ["theme"],
    queryFn: async () => {
      const { data } = await apiClient.get<{ data: ThemeResponse }>("/theme");
      return data.data;
    },
    staleTime: 5 * 60 * 1000,
  });
};

// ============================================================================
// Matchmaking (1v1)
// ============================================================================

export const useMatchmakingRequest = () => {
  return useMutation({
    mutationFn: async (
      payload: MatchmakingRequestPayload,
    ): Promise<MatchmakingRequestResponse> => {
      const client = await authorizedApiClient();
      const { data } = await client.post<
        ApiResponse<MatchmakingRequestResponse>
      >("/one-v-one-match/request", payload);
      return data.data;
    },
  });
};

// Sponsored variant: BE signs+sends on-chain, so the response has no base64Tx —
// success means the room/match is already on-chain.
export const useMatchmakingRequestSponsored = () => {
  return useMutation({
    mutationFn: async (
      payload: MatchmakingRequestPayload,
    ): Promise<MatchmakingRequestSponsoredResponse> => {
      const client = await authorizedApiClient();
      const { data } = await client.post<
        ApiResponse<MatchmakingRequestSponsoredResponse>
      >("/one-v-one-match/request-sponsored", payload);
      return data.data;
    },
  });
};

export const useMatchmakingCancel = () => {
  return useMutation({
    mutationFn: async (
      payload: MatchmakingCancelPayload,
    ): Promise<MatchmakingCancelResponse> => {
      const client = await authorizedApiClient();
      const { data } = await client.post<
        ApiResponse<MatchmakingCancelResponse>
      >("/one-v-one-match/cancel", payload);
      return data.data;
    },
    onSuccess: (_resp, vars) => {
      queryClient.invalidateQueries({
        queryKey: ["matchmaking", "details", vars.seed],
      });
      // Refund landed → refresh balances
      queryClient.invalidateQueries({ queryKey: ["solana.getUsdcBalance"] });
      queryClient.invalidateQueries({ queryKey: ["solana.getBalance"] });
    },
  });
};

type ErrShape = {
  response?: { status?: number; data?: { error?: string } };
  message?: string;
};

function getErrMessage(err: unknown): string {
  if (typeof err !== "object" || err === null) return "";
  const e = err as ErrShape;
  return e.response?.data?.error ?? e.message ?? "";
}

/**
 * The server-provided error string from an API response body (`{ error: string }`).
 * Unlike `getErrMessage`, this does NOT fall back to a generic `.message`, so it's
 * safe to surface verbatim in a toast — wallet/signing failures return "".
 */
export function getApiErrorMessage(err: unknown): string {
  if (typeof err !== "object" || err === null) return "";
  return (err as ErrShape).response?.data?.error ?? "";
}

function getErrStatus(err: unknown): number | null {
  if (typeof err !== "object" || err === null) return null;
  return (err as ErrShape).response?.status ?? null;
}

/**
 * Classifies the four distinct error responses from `/one-v-one-match/cancel`.
 * Each requires different UX (recoverable retry vs terminal "match starting").
 */
export type MatchmakingCancelErrorKind =
  | "confirming" // "Transaction is still confirming, wait a moment"
  | "reserving" // "Someone is currently challenging you, retry in a few seconds"
  | "challenge_committed" // "Someone has challenged you — match is starting"
  | "paired" // "Cannot cancel a paired 1v1 match"
  | "other";

export function classifyMatchmakingCancelError(
  err: unknown,
): MatchmakingCancelErrorKind {
  const msg = getErrMessage(err).toLowerCase();
  if (!msg) return "other";
  if (msg.includes("still confirming")) return "confirming";
  if (msg.includes("currently challenging")) return "reserving";
  if (msg.includes("match is starting")) return "challenge_committed";
  if (msg.includes("paired 1v1")) return "paired";
  return "other";
}

/** @deprecated Use classifyMatchmakingCancelError. Kept for back-compat. */
export function isReservingCancelError(err: unknown): boolean {
  return classifyMatchmakingCancelError(err) === "reserving";
}

/**
 * Classifies error responses from `/one-v-one-match/request`.
 * 429 → rate limited. 400 with "active 1v1 matches" → user hit the cap.
 * 400 with "banned" → equipped token is banned from squads.
 */
export type MatchmakingRequestErrorKind =
  | "rate_limited"
  | "active_match_cap"
  | "banned_token"
  | "other";

export function classifyMatchmakingRequestError(
  err: unknown,
): MatchmakingRequestErrorKind {
  if (getErrStatus(err) === 429) return "rate_limited";
  const msg = getErrMessage(err).toLowerCase();
  if (msg.includes("active 1v1 matches")) return "active_match_cap";
  if (msg.includes("banned")) return "banned_token";
  return "other";
}

/**
 * Classifies error responses from `/one-v-one-match/request-sponsored`.
 * Covers the sponsored-specific cases on top of the shared ones (rate limit,
 * active-match cap, generic validation).
 */
export type MatchmakingRequestSponsoredErrorKind =
  | "rate_limited"
  | "active_match_cap"
  | "no_sponsored_matches" // counter raced to 0
  | "temporarily_unavailable" // no funded sponsor / wallet empty / disabled
  | "matchmaking_disabled" // global 1v1 kill-switch
  | "other";

export function classifyMatchmakingRequestSponsoredError(
  err: unknown,
): MatchmakingRequestSponsoredErrorKind {
  if (getErrStatus(err) === 429) return "rate_limited";
  const msg = getErrMessage(err).toLowerCase();
  if (!msg) return "other";
  if (msg.includes("active 1v1 matches")) return "active_match_cap";
  if (msg.includes("no sponsored matches")) return "no_sponsored_matches";
  if (
    msg.includes("free 1v1 entries are temporarily unavailable") ||
    msg.includes("sponsored matches are temporarily unavailable") ||
    msg.includes("sponsor wallet is empty") ||
    msg.includes("sponsor wallet is low")
  )
    return "temporarily_unavailable";
  if (msg.includes("1v1 matchmaking is currently disabled"))
    return "matchmaking_disabled";
  return "other";
}

export const useMatchmakingDetails = (seed: string | null | undefined) => {
  return useQuery({
    queryKey: ["matchmaking", "details", seed ?? ""],
    queryFn: async (): Promise<MatchmakingDetails> => {
      const { data } = await apiClient.get<ApiResponse<MatchmakingDetails>>(
        `/one-v-one-match/details?seed=${encodeURIComponent(seed ?? "")}`,
      );
      return data.data;
    },
    enabled: !!seed,
  });
};

export const useMatchmakingHistory = (
  userId: number | string | null | undefined,
  options?: { enabled?: boolean },
) => {
  return useInfiniteQuery({
    queryKey: ["matchmaking", "history", userId ?? "global"],
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams();
      if (userId != null) params.append("userId", String(userId));
      if (pageParam) params.append("cursor", String(pageParam));
      const client = await authorizedApiClient();
      const { data } = await client.get<ApiResponse<MatchmakingHistoryRow[]>>(
        `/one-v-one-match/history${params.toString() ? `?${params.toString()}` : ""}`,
      );
      return {
        rows: data.data ?? [],
        nextCursor: data.pagination?.nextCursor ?? null,
        hasMore: data.pagination?.hasMore ?? false,
      };
    },
    initialPageParam: "" as string,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: options?.enabled ?? true,
  });
};

// VIP Program
export const useVip = (userId: number | undefined) => {
  return useQuery({
    queryKey: ["vip", userId ?? "catalog"],
    queryFn: async () => {
      const params = userId != null ? `?userId=${userId}` : "";
      const { data } = await apiClient.get<{
        success: boolean;
        data: VipResponse;
      }>(`/vip${params}`);
      return data.data;
    },
    staleTime: 30 * 1000,
  });
};

export const useClaimCashback = () => {
  return useMutation({
    mutationFn: async (bucket: "instant" | "daily" | "weekly" | "monthly") => {
      const client = await authorizedApiClient();
      const { data } = await client.post<{
        success: boolean;
        data: VipClaimResponse;
      }>("/vip/claim", { bucket });
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vip"] });
      queryClient.invalidateQueries({ queryKey: ["solana.getUsdcBalance"] });
      queryClient.invalidateQueries({ queryKey: ["stats"] });
    },
  });
};

// 64. Reward Trophy — manual trophy payout to the authenticated user.
export const useRewardTrophy = () => {
  return useMutation({
    mutationFn: async (input: {
      amount: number;
      purpose: string;
      metadata?: Record<string, unknown>;
    }) => {
      const client = await authorizedApiClient();
      const { data } = await client.post<
        ApiResponse<{
          rewardId: string;
          userId: number;
          rewardedAmount: number;
          finalMmr: number;
          remainingRewardsToday: number;
          remainingTrophiesToday: number;
        }>
      >("/reward/reward-trophy", input);
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stats"] });
    },
  });
};
